/**
 * RBAC UI helpers: <Can> gate, useHasPermission hook, <RequirePermission> route wrapper.
 *
 * Source of truth: AuthContext.member.permissions (loaded from /api/auth/me).
 * Super admins bypass everything (is_admin === true).
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { userHasPermission } from '../../config/permissions';
import { ShieldAlert } from 'lucide-react';

/**
 * Hook returning a stable `has(key)` function.
 * Usage: const has = useHasPermission(); if (has('members.edit')) { ... }
 */
export function useHasPermission() {
  const { member } = useAuth();
  return (key) => userHasPermission(member, key);
}

/**
 * Single-key boolean — most common case.
 * Usage: const canEdit = usePermission('members.edit');
 */
export function usePermission(key) {
  const { member } = useAuth();
  return userHasPermission(member, key);
}

/**
 * Render-prop / children gate. Hides children if user lacks `permission`.
 *
 *   <Can permission="members.edit">
 *     <Button>Edit</Button>
 *   </Can>
 *
 * Optional `fallback` prop to render an alternative.
 */
export function Can({ permission, children, fallback = null }) {
  const allowed = usePermission(permission);
  return allowed ? <>{children}</> : <>{fallback}</>;
}

/**
 * Route wrapper. Redirects to /admin (with toast) if user lacks `permission`.
 * Wrap inside <ProtectedRoute>.
 */
export function RequirePermission({ permission, children }) {
  const { member, isLoading } = useAuth();
  if (isLoading) return null;
  if (!member) return <Navigate to="/login" replace />;

  // Super admin bypass + view-permission check
  if (member.is_admin) return children;
  const allowed = userHasPermission(member, permission);
  if (!allowed) {
    return <AccessDenied permission={permission} />;
  }
  return children;
}

function AccessDenied({ permission }) {
  return (
    <div
      data-testid="access-denied"
      className="min-h-screen flex items-center justify-center bg-background px-6"
    >
      <div className="max-w-md text-center">
        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-red-50 flex items-center justify-center">
          <ShieldAlert className="w-8 h-8 text-red-500" />
        </div>
        <h1 className="text-2xl font-heading text-foreground mb-2">Access Denied</h1>
        <p className="text-sm text-muted-foreground mb-6">
          You don't have permission to view this section. Required:{' '}
          <code className="px-1.5 py-0.5 rounded bg-muted text-xs">{permission}</code>
        </p>
        <a
          href="/admin"
          data-testid="access-denied-back"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-white text-sm font-medium hover:opacity-90"
        >
          Back to Admin
        </a>
      </div>
    </div>
  );
}
